'use client'
import { TranslationFunction } from '@/src/types/translations'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import FlightLandIcon from '@mui/icons-material/FlightLand'
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff'
import SearchIcon from '@mui/icons-material/Search'
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    TextField,
    Typography
} from '@mui/material'
import React from 'react'

interface AirportSelectorProps {
    type: 'from' | 'to'
    title: string
    placeholder: string
    searchQuery: string
    loading: boolean
    options: any[]
    showNoResults: boolean
    inputRef: React.RefObject<HTMLInputElement>
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onOptionSelect: (option: any) => void
    onClear: () => void
    onClose: () => void
    onInputFocus: (e: React.FocusEvent<HTMLInputElement>) => void
    t: TranslationFunction<'common'>
    isPopularAirports: boolean
}

const AirportSelector: React.FC<AirportSelectorProps> = ({
    type,
    title,
    placeholder,
    searchQuery,
    loading,
    options,
    showNoResults,
    inputRef,
    onSearchChange,
    onOptionSelect,
    onClear,
    onClose,
    onInputFocus,
    t,
    isPopularAirports
}) => {
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
            {/* Header with Back Button */}
            <Box
                sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
            >
                <IconButton onClick={onClose} size="small" sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
            </Box>

            {/* Search Input */}
            <Box sx={{ p: 2 }}>
                <TextField
                    inputRef={inputRef}
                    fullWidth
                    autoFocus
                    placeholder={placeholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={onClear}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                        },
                        '& input': {
                            cursor: 'text'
                        }
                    }}
                    onFocus={onInputFocus}
                />
            </Box>

            <Divider />

            {/* Results Section */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : options.length > 0 ? (
                    <List sx={{ p: 0 }}>
                        {isPopularAirports && (
                            <Typography p={1} variant="body2" color="textSecondary" fontWeight={500}>
                                {t('popularAirports')}
                            </Typography>
                        )}
                        {options.map((option, index) => (
                            <ListItem
                                key={`${option.payload.airportCode}-${index}`}
                                onClick={() => onOptionSelect(option)}
                                sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    py: 0,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: 'action.hover'
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    {type === 'from' ? (
                                        <FlightTakeoffIcon color="action" />
                                    ) : (
                                        <FlightLandIcon color="action" />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" fontWeight={500}>
                                            {option.payload.cityName}, {option.payload.countryName}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary">
                                            {option.payload.airportName}
                                        </Typography>
                                    }
                                />
                                <Paper
                                    elevation={0}
                                    sx={{
                                        bgcolor: 'grey.100',
                                        px: 1.5,
                                        // py: 0.5,
                                        borderRadius: 1
                                    }}
                                >
                                    <Typography variant="caption" fontWeight={600}>
                                        {option.payload.airportCode}
                                    </Typography>
                                </Paper>
                            </ListItem>
                        ))}
                    </List>
                ) : showNoResults ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">{t('noAirportFound')}</Typography>
                    </Box>
                ) : null}
            </Box>
        </Box>
    )
}

export { AirportSelector }
export type { AirportSelectorProps }
